/**
 * Portal Service
 * ==============
 * Two API groups:
 *   admin.*   — staff endpoints (uses staff JWT from localStorage)
 *   parent.*  — parent portal endpoints (uses ParentToken from localStorage)
 */

const BASE = process.env.VUE_APP_API_BASE || 'https://0x01source.com:9002/api'
const PORTAL = `${BASE}/portal`

// ── Token helpers ────────────────────────────────────────────────────────────

function staffHeaders() {
  const token = localStorage.getItem('staff_access')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

function parentHeaders() {
  const token = localStorage.getItem('parent_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `ParentToken ${token}` } : {}),
  }
}

// ── Core request helpers ─────────────────────────────────────────────────────

async function request(url, options = {}) {
  const res = await fetch(url, options)
  if (!res.ok) {
    const text = await res.text()
    let detail = text
    try { detail = JSON.parse(text)?.detail || JSON.parse(text)?.error || text } catch {}
    throw Object.assign(new Error(detail), { status: res.status })
  }
  if (res.status === 204) return null
  return res.json()
}

async function getBlob(url, params = {}, headers = staffHeaders()) {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString()
  const fullUrl = `${url}${qs ? '?' + qs : ''}`
  const res = await fetch(fullUrl, { headers })
  if (!res.ok) {
    const text = await res.text()
    let detail = text
    try { detail = JSON.parse(text)?.detail || JSON.parse(text)?.error || text } catch {}
    throw Object.assign(new Error(detail), { status: res.status })
  }
  return res.blob()
}

function get(url, params = {}, headers = staffHeaders()) {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString()
  return request(`${url}${qs ? '?' + qs : ''}`, { headers })
}

function post(url, data, headers = staffHeaders()) {
  return request(url, { method: 'POST', headers, body: JSON.stringify(data) })
}

function patch(url, data, headers = staffHeaders()) {
  return request(url, { method: 'PATCH', headers, body: JSON.stringify(data) })
}

function del(url, headers = staffHeaders()) {
  return request(url, { method: 'DELETE', headers })
}

async function uploadFile(url, file, fieldName = 'file', headers = staffHeaders()) {
  const fd = new FormData()
  fd.append(fieldName, file)
  const { 'Content-Type': _, ...h } = headers  // remove Content-Type; let browser set it
  const res = await fetch(url, { method: 'POST', headers: h, body: fd })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text)
  }
  return res.json()
}

// ── Admin API ────────────────────────────────────────────────────────────────

export const adminPortal = {

  // Settings
  getSettings: ()       => get(`${PORTAL}/settings/`),
  updateSettings: (d)   => patch(`${PORTAL}/settings/`, d),

  // Parents
  listParents: (p)      => get(`${PORTAL}/parents/`, p),
  getParent: (id)       => get(`${PORTAL}/parents/${id}/`),
  createParent: (d)     => post(`${PORTAL}/parents/`, d),
  updateParent: (id, d) => patch(`${PORTAL}/parents/${id}/`, d),
  deleteParent: (id)    => del(`${PORTAL}/parents/${id}/`),
  resetPassword: (id, d) => post(`${PORTAL}/parents/${id}/reset_password/`, d),
  exportParents: ()     => `${PORTAL}/parents/export/`,
  importParents: (file) => uploadFile(`${PORTAL}/parents/import_xlsx/`, file),

  // Students
  listStudents: (p)     => get(`${PORTAL}/students/`, p),
  createStudent: (d)    => post(`${PORTAL}/students/`, d),
  updateStudent: (id, d) => patch(`${PORTAL}/students/${id}/`, d),
  deleteStudent: (id)   => del(`${PORTAL}/students/${id}/`),

  // Invoices
  listInvoices: (p)       => get(`${PORTAL}/invoices/`, p),
  getInvoice: (id)        => get(`${PORTAL}/invoices/${id}/`),
  createInvoice: (d)      => post(`${PORTAL}/invoices/`, d),
  updateInvoice: (id, d)  => patch(`${PORTAL}/invoices/${id}/`, d),
  deleteInvoice: (id)     => del(`${PORTAL}/invoices/${id}/`),
  markPaid: (id)          => post(`${PORTAL}/invoices/${id}/mark_paid/`, {}),
  markCancelled: (id)     => post(`${PORTAL}/invoices/${id}/mark_cancelled/`, {}),
  exportInvoices: ()      => `${PORTAL}/invoices/export/`,
  importInvoices: (file)  => uploadFile(`${PORTAL}/invoices/import_xlsx/`, file),

  // Global import/export
  exportAll: ()           => `${PORTAL}/export-all/`,
  importTemplate: ()      => `${PORTAL}/import-template/`,

  // Checks
  listChecks: (p)       => get(`${PORTAL}/checks/`, p),
  createCheck: (d)      => post(`${PORTAL}/checks/`, d),
  updateCheck: (id, d)  => patch(`${PORTAL}/checks/${id}/`, d),
  deleteCheck: (id)     => del(`${PORTAL}/checks/${id}/`),
  markCleared: (id)     => post(`${PORTAL}/checks/${id}/mark_cleared/`, {}),
  markBounced: (id)     => post(`${PORTAL}/checks/${id}/mark_bounced/`, {}),

  // Payments (read-only + refresh)
  listPayments: (p)          => get(`${PORTAL}/payments/`, p),
  refreshPaymentStatus: (id) => post(`${PORTAL}/payments/${id}/refresh_status/`, {}),
  exportPayments: (p)        => getBlob(`${PORTAL}/payments/export/`, p),

  // Invoice PDF download URL (for <a :href="..."> or fetch)
  invoiceUrl: (id) => `${PORTAL}/public/pay/${id}/invoice/`,

  // Sample receipt PDF (authenticated blob)
  getSampleReceipt: () => getBlob(`${PORTAL}/sample-receipt/`),
}

// ── Parent Portal API ────────────────────────────────────────────────────────

export const parentPortal = {

  // Auth
  login: (d)      => post(`${PORTAL}/auth/login/`, d, { 'Content-Type': 'application/json' }),

  // Profile & data
  me:        ()   => get(`${PORTAL}/me/`,          {}, parentHeaders()),
  invoices:  ()   => get(`${PORTAL}/me/invoices/`, {}, parentHeaders()),
  checks:    ()   => get(`${PORTAL}/me/checks/`,   {}, parentHeaders()),
  payments:  ()   => get(`${PORTAL}/me/payments/`, {}, parentHeaders()),

  // Skiply payment
  initiatePayment: (d)    => post(`${PORTAL}/pay/initiate/`, d, parentHeaders()),
  paymentStatus: (id)     => get(`${PORTAL}/pay/${id}/status/`, {}, parentHeaders()),
}

// ── Public Payment API (no auth) ─────────────────────────────────────────────

export const publicPayment = {
  getPublicSettings: () => get(`${PORTAL}/public/settings/`, {}, { 'Content-Type': 'application/json' }),
  initiate: (d)       => post(`${PORTAL}/public/pay/initiate/`, d, { 'Content-Type': 'application/json' }),
  status: (id)        => get(`${PORTAL}/public/pay/${id}/status/`, {}, { 'Content-Type': 'application/json' }),
  detail: (id)        => get(`${PORTAL}/public/pay/${id}/`, {}, { 'Content-Type': 'application/json' }),
}

// ── Token management ─────────────────────────────────────────────────────────

export function saveParentToken(token) {
  localStorage.setItem('parent_token', token)
}

export function clearParentToken() {
  localStorage.removeItem('parent_token')
  localStorage.removeItem('parent_profile')
}

export function getParentToken() {
  return localStorage.getItem('parent_token')
}

export function saveParentProfile(profile) {
  localStorage.setItem('parent_profile', JSON.stringify(profile))
}

export function getParentProfile() {
  try {
    return JSON.parse(localStorage.getItem('parent_profile'))
  } catch {
    return null
  }
}
